import { Injectable, computed, signal } from '@angular/core';
import { V2AirportTab } from '@v2/shared/airport-tabs/v2-airport-tab.model';
import { AirportModel } from 'app/airport/models/airport.model';

/**
 * The open-airport strip and the recents list behind the start page.
 *
 * Spec §6.3: opening an airport adds it to a persistent tab strip, tabs
 * survive until closed, and the active one is mirrored into the URL. The URL
 * is the single source of truth for *which* tab is active -- this service only
 * owns *which tabs exist* -- so a briefing is linkable and the back button
 * moves between airports rather than out of the page.
 *
 * `providedIn: 'root'` rather than route-scoped: a route-scoped instance is
 * destroyed the moment somebody steps out to the estimator, and coming back to
 * an empty strip is the opposite of "tabs persist". It holds a bounded amount
 * of state -- the strip plus at most `V2_BRIEFING_RECENTS_LIMIT` records.
 *
 * SSR-safe: no `window`, `document` or `localStorage`. Nothing here survives a
 * reload, which is why the active airport is in the URL and not in here.
 */

/** How many airports the start page offers back. Beyond this it is a list, not a shortcut. */
export const V2_BRIEFING_RECENTS_LIMIT = 8;

/** The airport id, as a string, is the tab key and the URL segment. One identity, not two. */
export function briefingTabKey(airport: { id: number | string }): string {
  return String(airport.id);
}

@Injectable({ providedIn: 'root' })
export class V2BriefingTabsService {
  private readonly _tabs = signal<V2AirportTab[]>([]);
  private readonly _recents = signal<AirportModel[]>([]);

  /** No `status`: a briefing tab has no state machine, the estimator's has. */
  readonly tabs = this._tabs.asReadonly();
  readonly recents = this._recents.asReadonly();
  readonly count = computed(() => this._tabs().length);

  has(key: string): boolean {
    return this._tabs().some(tab => tab.key === key);
  }

  /**
   * Add an airport to the strip, or refresh the record already under its key.
   *
   * The refresh matters: a tab opened from a search result carries the list
   * payload, and the detail endpoint answers with a fuller record a moment
   * later. Replacing it keeps one object per airport rather than two that
   * disagree.
   */
  open(airport: AirportModel): string {
    const key = briefingTabKey(airport);
    const tabs = this._tabs();
    const index = tabs.findIndex(tab => tab.key === key);

    // Idempotent on purpose. This is called from an effect that runs whenever
    // the loaded record changes, and re-storing the same object would publish a
    // fresh array identity every time. Signals compare by Object.is, so that
    // notifies every reader on every pass -- and where a reader's own write
    // feeds back into the record, the effect never settles.
    if (index >= 0 && tabs[index].airport === airport) {
      return key;
    }

    if (index >= 0) {
      const next = [...tabs];
      next[index] = { key, airport };
      this._tabs.set(next);
    } else {
      this._tabs.set([...tabs, { key, airport }]);
    }

    this.remember(airport);

    return key;
  }

  /**
   * Close one tab and say which tab should take over.
   *
   * Returns the key the page should navigate to if the closed tab was the
   * active one -- the tab that slid into its place, or the new last one --
   * and `null` when the strip is now empty and the start page is the answer.
   * The page decides whether to act on it; closing a background tab must not
   * move anybody.
   */
  close(key: string): string | null {
    const tabs = this._tabs();
    const index = tabs.findIndex(tab => tab.key === key);
    if (index < 0) { return null; }

    const next = tabs.filter(tab => tab.key !== key);
    this._tabs.set(next);

    if (next.length === 0) { return null; }

    return next[Math.min(index, next.length - 1)].key;
  }

  /** Everything closed at once. Used by tests and by a full reset of the page. */
  clear(): void {
    this._tabs.set([]);
  }

  /**
   * Most recent first, deduplicated, capped.
   *
   * Recents outlive the strip on purpose: closing a tab is "I am done with
   * this for now", not "I was never here". This is what the signed-in start
   * page offers instead of a list of popular airports we would have invented.
   */
  private remember(airport: AirportModel): void {
    const key = briefingTabKey(airport);
    const current = this._recents();

    // Same reasoning as open(): if this airport is already at the head, the
    // list has not changed and republishing it only wakes readers.
    if (current.length && briefingTabKey(current[0]) === key && current[0] === airport) {
      return;
    }

    const rest = current.filter(entry => briefingTabKey(entry) !== key);

    this._recents.set([airport, ...rest].slice(0, V2_BRIEFING_RECENTS_LIMIT));
  }
}
